import {useQueryClient} from '@tanstack/react-query';
import {
  type useGetEntryManagement,
  useUpdateEntryManagement,
} from '../../../core/repositories/mypageEntryManagementsRepository';
import type {FormDataEntryProgressStep} from '../components/Container/Presenter/Progress/ProgressStepDrawer';
import {
  ENTRY_SOURCE_TYPE_MANUAL,
  type EntryFormOriginType,
  type StepCreationTriggerType,
} from '../constants/entrySourceTypes';
import {invalidateEntryManagementCaches} from './cacheInvalidationHelpers';

export const useProgressStepFormUpdater = () => {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateEntryManagement();

  const updateForm = async (
    entryManagementId: number,
    formData: FormDataEntryProgressStep,
  ) => {
    if (!formData.stepId) {
      return {
        success: false,
        error: 'ステップIDが指定されていません。',
      };
    }

    // 現在のエントリー管理詳細を取得
    const currentData = queryClient.getQueryData<
      Awaited<ReturnType<typeof useGetEntryManagement>>['data']
    >(['entryManagement', entryManagementId]);

    if (!currentData?.ok) {
      return {
        success: false,
        error: 'エントリー管理データが取得できませんでした。',
      };
    }

    const entryDetail = currentData.value;

    // 変更対象のステップを取得
    const targetStep = entryDetail.steps?.find(
      (step) => step.id === formData.stepId,
    );
    if (!targetStep) {
      return {
        success: false,
        error: 'ステップが見つかりませんでした。',
      };
    }

    // 更新データを構築（変更されたステップのみ）
    // tri-state対応:
    // - undefined: フィールドを含めない → 既存値を保持
    // - null or "": nullを送信 → 値を削除
    // - 値あり: その値を送信 → 値を更新

    const updatedStep = {
      id: formData.stepId,
      stepName: formData.stepName,
      stepOrder: formData.stepOrder,
      sourceType: targetStep.sourceType as EntryFormOriginType | undefined, // 既存値を保持
      entrySourceType: (targetStep.entrySourceType ||
        ENTRY_SOURCE_TYPE_MANUAL) as StepCreationTriggerType, // 既存値を保持、なければデフォルト
      // オプショナルフィールドの処理（tri-state対応）
      ...(formData.notes !== undefined && {
        notes: formData.notes || null,
      }),
      ...(formData.result !== undefined && {
        result: formData.result || null,
      }),
      ...(formData.deadline !== undefined && {
        deadline: formData.deadline || null,
      }),
      ...(formData.startDate !== undefined && {
        startDate: formData.startDate || null,
      }),
      ...(formData.endDate !== undefined && {
        endDate: formData.endDate || null,
      }),
      ...(formData.place !== undefined && {place: formData.place || null}),
      // entryTitle, entrySourceUrl, entryDate, stepDate は
      // フォームに含まれていないので、既存値を保持（フィールドを含めない）
    };

    const payload = {
      id: entryManagementId,
      companyID: entryDetail.companyID || 0,
      companyName: entryDetail.companyName,
      steps: [updatedStep],
    };

    // PUT /entry-managements/{id} でステップ更新（変更されたステップのみ）
    const result = await updateMutation.mutateAsync(payload);

    if (result.ok) {
      // キャッシュ無効化
      await invalidateEntryManagementCaches(queryClient, entryManagementId);

      return {
        success: true,
        googleSyncFailed: result.value.googleSyncResult?.success === false,
      };
    }

    return {
      success: false,
      error: 'ステップの更新に失敗しました。',
    };
  };

  return {
    updateForm,
    isLoading: updateMutation.isLoading,
  };
};
